export const endpoints = {
  seller: {
    auth: {
      switchStore: "/seller/auth/switch-store",
      logout: "/seller/auth/logout",
      me: "/seller/auth/me",
    },
    store: "/seller/store",
    storeSettings: "/seller/store/settings",
    // Separate from storeSettings: the access token must never travel back in
    // a settings response, so the connection has its own write-only route.
    metaCapi: "/seller/store/meta-capi",
    tagManager: "/seller/store/tag-manager",
    storeShippingRules: "/seller/store/shipping-rules",
    storeShippingRuleById: (id: string) => `/seller/store/shipping-rules/${id}`,
    storeGovernorates: "/seller/store/governorates",
    storeAreaRates: "/seller/store/area-rates",
    storeAreaRateById: (areaId: string) =>
      `/seller/store/area-rates/${areaId}`,
    storeAreaRatesReset: "/seller/store/area-rates/reset",
    customDomains: "/seller/custom-domains",
    customDomainsConfirm: "/seller/custom-domains/confirm",
    customDomainsRefresh: "/seller/custom-domains/refresh",
    profile: "/seller/profile",
    password: "/seller/password",
    paymentOptions: "/seller/payment-options",
    paymentOptionById: (id: string) => `/seller/payment-options/${id}`,
    categories: "/seller/categories",
    categoryById: (id: string) => `/seller/categories/${id}`,
    products: "/seller/products",
    productById: (id: string) => `/seller/products/${id}`,
    productVariants: (productId: string) =>
      `/seller/products/${productId}/variants`,
    productVariantCosts: (productId: string) =>
      `/seller/products/${productId}/variant-costs`,
    productVariantsBulkDelete: (productId: string) =>
      `/seller/products/${productId}/variants/bulk-delete`,
    productVariant: (productId: string, variantId: string) =>
      `/seller/products/${productId}/variants/${variantId}`,
    productOptionValue: (productId: string, valueId: string) =>
      `/seller/products/${productId}/option-values/${valueId}`,
    productImagesById: (id: string) => `/seller/products/${id}/images`,
    /** The store's featured list, whole and ordered. A catalog write, so it
     *  needs no design publish to reach the storefront. */
    productsFeatured: "/seller/products/featured",
    productUploads: "/seller/uploads/products",
    /** Bulk catalogue import/export. Registered ahead of `/seller/products/:id`
     *  on the backend, so `productsExport` resolves to the export handler. */
    productImportTemplate: "/seller/products/import/template",
    productImportAnalyze: "/seller/products/import/analyze",
    productImportConfirm: (jobId: string) =>
      `/seller/products/import/${jobId}/confirm`,
    productImportJob: (jobId: string) => `/seller/products/import/${jobId}`,
    productsExport: "/seller/products/export",
    productImportUploads: "/seller/uploads/product-import",
    storeLogoUploads: "/seller/uploads/store-logo",
    shareCardUploads: "/seller/uploads/share-card",
    categoryImageUploads: "/seller/uploads/category-image",
    orders: "/seller/orders",
    /** The first-run guided tour's practice order (GET reads, POST creates, DELETE removes). */
    sampleOrder: "/seller/orders/sample",
    ordersSmart: "/seller/orders/smart",
    orderStatusCounts: "/seller/orders/status-counts",
    orderById: (id: string) => `/seller/orders/${id}`,
    orderStatus: (id: string) => `/seller/orders/${id}/status`,
    orderStatusUndo: (id: string) => `/seller/orders/${id}/status/undo`,
    orderCodShortfallAcknowledge: (id: string) =>
      `/seller/orders/${id}/cod-shortfall/acknowledge`,
    orderCarrierReference: (id: string) => `/seller/orders/${id}/carrier-reference`,
    ordersActivityToday: "/seller/orders/activity-today",
    orderDraftConfirm: (id: string) => `/seller/orders/${id}/draft/confirm`,
    orderDraftDiscard: (id: string) => `/seller/orders/${id}/draft/discard`,
    orderProof: (id: string) => `/seller/orders/${id}/proof`,
    /** Returns & exchanges. `returns/summary` is a literal segment the backend
     *  declares BEFORE its `:orderId` route — do not reorder it there. */
    orderReturnsSummary: "/seller/orders/returns/summary",
    orderReturns: (orderId: string) => `/seller/orders/${orderId}/returns`,
    orderReturnPhotos: (orderId: string, returnId: string) =>
      `/seller/orders/${orderId}/returns/${returnId}/photos`,
    orderReturnApprove: (orderId: string, returnId: string) =>
      `/seller/orders/${orderId}/returns/${returnId}/approve`,
    orderReturnReject: (orderId: string, returnId: string) =>
      `/seller/orders/${orderId}/returns/${returnId}/reject`,
    orderReturnReceived: (orderId: string, returnId: string) =>
      `/seller/orders/${orderId}/returns/${returnId}/received`,
    orderReturnResolve: (orderId: string, returnId: string) =>
      `/seller/orders/${orderId}/returns/${returnId}/resolve`,
    orderPaymentTransaction: (id: string) =>
      `/seller/orders/${id}/payment-transaction`,
    orderRefund: (id: string) => `/seller/orders/${id}/refund`,
    orderInvoice: (id: string) => `/seller/orders/${id}/invoice`,
    orderShippingFee: (id: string) => `/seller/orders/${id}/shipping-fee`,
    orderRequestPayment: (id: string) =>
      `/seller/orders/${id}/request-payment`,
    orderReviewApprove: (id: string) =>
      `/seller/orders/${id}/payment-review/approve`,
    orderReviewReject: (id: string) =>
      `/seller/orders/${id}/payment-review/reject`,
    /** Store-wide gateway transactions (all orders), not the per-order view. */
    transactions: "/seller/transactions",
    transactionById: (id: string) => `/seller/transactions/${id}`,
    storeSubscription: "/seller/store/subscription",
    storeWallet: "/seller/store/wallet",
    storeWalletTransactions: "/seller/store/wallet/transactions",
    storeWalletTopUpRequests: "/seller/store/wallet/top-up-requests",
    /** Short-lived signed URL for one top-up request's proof. The store is
     *  taken from the session server-side — there is no store id to pass. */
    storeWalletTopUpRequestProofUrl: (paymentId: string) =>
      `/seller/store/wallet/top-up-requests/${paymentId}/proof-url`,
    walletTopUpProofUploads: "/seller/uploads/wallet-top-up-proof",
    storePolicyTemplates: "/seller/store/policy-templates",
    onboardingGenerate: "/seller/onboarding/generate",
    onboardingApply: "/seller/onboarding/apply",
    onboardingCategorySuggestions: "/seller/onboarding/category-suggestions",
    checkoutLinks: (storeSlug: string) =>
      `/seller/stores/${storeSlug}/checkout-links`,
    preparedCheckoutLinks: (storeSlug: string) =>
      `/seller/stores/${storeSlug}/checkout-links/prepared`,
    preparedCheckoutLink: (storeSlug: string, cartId: string) =>
      `/seller/stores/${storeSlug}/checkout-links/prepared/${encodeURIComponent(cartId)}`,
    preparedCartAction: (storeSlug: string, cartId: string, action: string) =>
      `/seller/stores/${storeSlug}/checkout-links/prepared/${encodeURIComponent(cartId)}/${action}`,
    fcmTokens: "/seller/fcm-tokens",
    bostaConnection: "/seller/integrations/bosta",
    bostaConnectionActive: "/seller/integrations/bosta/active",
    bostaConnectionOpenPackage: "/seller/integrations/bosta/open-package",
    bostaWebhookRotate: "/seller/integrations/bosta/webhook/rotate",
    jntConnection: "/seller/integrations/jnt",
    jntConnectionActive: "/seller/integrations/jnt/active",
    shipbluConnection: "/seller/integrations/shipblu",
    shipbluConnectionActive: "/seller/integrations/shipblu/active",
    shipbluWebhookRotate: "/seller/integrations/shipblu/webhook/rotate",
    yfsConnection: "/seller/integrations/yfs",
    yfsConnectionActive: "/seller/integrations/yfs/active",
    yfsWebhookRotate: "/seller/integrations/yfs/webhook/rotate",
    aramexConnection: "/seller/integrations/aramex",
    aramexConnectionActive: "/seller/integrations/aramex/active",
    mylerzConnection: "/seller/integrations/mylerz",
    mylerzConnectionActive: "/seller/integrations/mylerz/active",
    speedafConnection: "/seller/integrations/speedaf",
    speedafConnectionActive: "/seller/integrations/speedaf/active",
    // Online payment gateways (paymob | paytabs | kashier | fawry) share one
    // shape; the provider slug selects the adapter on the backend.
    paymentGatewayConnection: (provider: string) =>
      `/seller/integrations/${provider}`,
    paymentGatewayActive: (provider: string) =>
      `/seller/integrations/${provider}/active`,
    paymentGatewayPresentation: (provider: string) =>
      `/seller/integrations/${provider}/presentation`,
    orderShipment: (orderId: string) => `/seller/orders/${orderId}/shipment`,
    orderShipmentJnt: (orderId: string) =>
      `/seller/orders/${orderId}/shipment/jnt`,
    orderShipmentShipblu: (orderId: string) =>
      `/seller/orders/${orderId}/shipment/shipblu`,
    orderShipmentYfs: (orderId: string) =>
      `/seller/orders/${orderId}/shipment/yfs`,
    orderShipmentAramex: (orderId: string) =>
      `/seller/orders/${orderId}/shipment/aramex`,
    orderShipmentMylerz: (orderId: string) =>
      `/seller/orders/${orderId}/shipment/mylerz`,
    orderShipmentSpeedaf: (orderId: string) =>
      `/seller/orders/${orderId}/shipment/speedaf`,
    orderShipmentOptions: (orderId: string) =>
      `/seller/orders/${orderId}/shipment/options`,
    orderShipmentRefresh: (orderId: string) =>
      `/seller/orders/${orderId}/shipment/refresh`,
    orderShipmentAwb: (orderId: string, lang: "ar" | "en") =>
      `/seller/orders/${orderId}/shipment/awb?lang=${lang}`,
    theme: "/seller/theme",
    themeApplyBrandColors: "/seller/theme/apply-brand-colors",
    // The store's brand — colours + typography — from the Store Settings
    // branding panel. Writes LIVE with no draft step, which is the whole point:
    // brand is one true value and must not need a design publish (Rule 17).
    themeBrand: "/seller/theme/brand",
    themeDraft: "/seller/theme/draft",
    themePublish: "/seller/theme/publish",
    themeDiscard: "/seller/theme/discard",
    themeReset: "/seller/theme/reset",
    themePreviewToken: "/seller/theme/preview-token",
    // The handover lock. Owner-only, enforced from Store.sellerId rather than
    // a permission — see the controller docblock.
    themeFreeze: "/seller/theme/freeze",
    themeUnfreeze: "/seller/theme/unfreeze",
    themeAssets: "/seller/theme/assets",
    themeAssetUploadUrl: "/seller/uploads/theme-asset",
    themePublications: "/seller/theme/publications",
    themePublicationRestore: (id: string) =>
      `/seller/theme/publications/${id}/restore`,
    // Saved looks — the store's OWN library of designs it has published
    // (Round 2). Distinct from the platform catalogue below.
    themeSavedLooks: "/seller/theme/saved-looks",
    themeSavedLook: (id: string) => `/seller/theme/saved-looks/${id}`,
    themeSavedLookRestore: (id: string) =>
      `/seller/theme/saved-looks/${id}/restore`,
    // Theme gallery (composable themes S4)
    themesGallery: "/seller/themes",
    themeApply: (themeId: string) => `/seller/themes/${themeId}/apply`,
    // Theme version-update nudge (composable themes S6)
    themeUpdateAvailable: "/seller/themes/update-available",
    reviews: "/seller/reviews",
    reviewById: (id: string) => `/seller/reviews/${id}`,
    customers: "/seller/customers",
    customerById: (id: string) => `/seller/customers/${id}`,
    coupons: "/seller/coupons",
    couponById: (id: string) => `/seller/coupons/${id}`,
    roles: "/seller/roles",
    roleById: (id: string) => `/seller/roles/${id}`,
    rolePermissions: "/seller/roles/permissions",
    members: "/seller/members",
    memberById: (id: string) => `/seller/members/${id}`,
    invites: "/seller/invites",
    inviteById: (id: string) => `/seller/invites/${id}`,
    search: "/seller/search",
    /** SQL-aggregated dashboard metrics; takes `?range=24h|7d|30d|90d`. */
    analyticsDashboard: "/seller/analytics/dashboard",
  },
  public: {
    invite: (token: string) => `/public/invite/${token}`,
    acceptInvite: (token: string) => `/public/invite/${token}/accept`,
    storeInvite: (token: string) => `/public/store-invite/${token}`,
    acceptStoreInvite: (token: string) =>
      `/public/store-invite/${token}/accept`,
    sellerAuth: {
      login: "/public/seller/auth/login",
      impersonationHandoff: "/public/seller/auth/impersonation-handoff",
    },
    storeIdentity: (storeSlug: string) => `/public/stores/${storeSlug}`,
    storeCategories: (storeSlug: string) =>
      `/public/stores/${storeSlug}/categories`,
    storeProducts: (storeSlug: string) =>
      `/public/stores/${storeSlug}/products`,
    productByRef: (storeSlug: string, productRef: string) =>
      `/public/stores/${storeSlug}/products/${productRef}`,
    storePaymentOptions: (storeSlug: string) =>
      `/public/stores/${storeSlug}/payment-options`,
    storePolicies: (storeSlug: string) =>
      `/public/stores/${storeSlug}/policies`,
    storeShippingInfo: (storeSlug: string) => `/public/stores/${storeSlug}/shipping-info`,
    storeDepositSettings: (storeSlug: string) => `/public/stores/${storeSlug}/deposit-settings`,
    storeAreas: (storeSlug: string, governorateId: string) => `/public/stores/${storeSlug}/areas?governorateId=${encodeURIComponent(governorateId)}`,
    storeShippingCities: (storeSlug: string) =>
      `/public/stores/${storeSlug}/shipping-cities`,
    createDraftOrder: (storeSlug: string) =>
      `/public/stores/${storeSlug}/orders`,
    confirmDraftOrder: (storeSlug: string, draftOrderId: string) =>
      `/public/stores/${storeSlug}/orders/${draftOrderId}`,
    paymentProofUploads: (storeSlug: string) =>
      `/public/stores/${storeSlug}/payment-proof-uploads`,
    trackOrder: "/public/orders/track",
    trackOrderMagic: "/public/orders/tracking-order",
  },
} as const;
